import React, { useEffect, useMemo, useState } from 'react';
import { Card } from '../../components/ui/Card';
import { useAuth } from '../../context/AuthContext';
import { RefreshCw, ShoppingCart, TrendingUp, Filter, Sun, DollarSign, LayoutGrid } from 'lucide-react';

const API_BASE = 'https://api.glowytics.com';

const GRAINS = [
  { key: 'month', label: 'Monthly' },
  { key: 'week', label: 'Weekly' },
  { key: 'day', label: 'Daily' },
];

const COUNT_OPTIONS = {
  month: [
    { value: 3, label: 'Last 3 months' },
    { value: 6, label: 'Last 6 months' },
    { value: 12, label: 'Last 12 months' },
  ],
  week: [
    { value: 8, label: 'Last 8 weeks' },
    { value: 12, label: 'Last 12 weeks' },
    { value: 26, label: 'Last 26 weeks' },
  ],
  day: [
    { value: 7, label: 'Last 7 days' },
    { value: 14, label: 'Last 14 days' },
    { value: 30, label: 'Last 30 days' },
  ],
};

const DEFAULT_COUNT = { month: 6, week: 12, day: 14 };

const GRAIN_NOUN = { month: 'months', week: 'weeks', day: 'days' };
const GRAIN_ADVERB = { month: 'month over month', week: 'week over week', day: 'day over day' };
const COUNT_PARAM = { month: 'months', week: 'weeks', day: 'days' };

const METRICS = [
  { key: 'orders', label: 'Orders', icon: ShoppingCart },
  { key: 'revenue', label: 'Revenue', icon: TrendingUp },
  { key: 'cost', label: 'Ad Spend', icon: DollarSign },
  { key: 'combo', label: 'Orders + Spend', icon: LayoutGrid },
  { key: 'funnel', label: 'Checkout → Buy', icon: Filter },
];

// Field used to color the heatmap for the active metric.
const COLOR_FIELD = { orders: 'orders', revenue: 'revenue', cost: 'cost', combo: 'orders', funnel: 'orders' };

// Intensity-ramp hues for the dual "Orders + Spend" view.
const HUE = { blue: '37, 99, 235', orange: '234, 88, 12' };

const fmtNumber = (n) => Number(n || 0).toLocaleString('en-US');
const fmtMoney = (n, sym) => `${sym}${Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
const fmtPercent = (n) => `${(Number(n || 0) * 100).toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;

const currencySymbol = (cur) => (cur === 'TRY' ? '₺' : cur === 'USD' ? '$' : cur === 'EUR' ? '€' : `${cur || ''} `);

// Intensity ramp for a given rgb hue. t in [0,1].
const rampColor = (t, rgb = HUE.blue) => {
  if (!(t > 0)) return { background: '#f8fafc', color: '#cbd5e1' };
  const alpha = 0.12 + 0.88 * Math.sqrt(t); // sqrt so low values stay visible
  return {
    background: `rgba(${rgb}, ${alpha.toFixed(3)})`,
    color: alpha > 0.55 ? '#ffffff' : '#1e293b',
  };
};
// Single-hue (blue) ramp — kept for the non-combo metrics.
const cellColor = (t) => rampColor(t, HUE.blue);

// Compact money/number for tight cells: 1234 → "1K", 980 → "980".
const compact = (v) => (v >= 1000 ? `${Math.round(v / 1000)}K` : fmtNumber(Math.round(v)));

export default function HourlyOrderAnalysis() {
  const { selectedBrand } = useAuth();
  const [grain, setGrain] = useState('month');
  const [count, setCount] = useState(DEFAULT_COUNT.month);
  const [metric, setMetric] = useState('orders');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [payload, setPayload] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const changeGrain = (g) => {
    if (g === grain) return;
    setGrain(g);
    setCount(DEFAULT_COUNT[g]);
  };

  const fetchData = async () => {
    if (!selectedBrand?.id) return;
    try {
      setLoading(true);
      setError('');
      const url = new URL(`${API_BASE}/api/google-analytics/ga4/hourly-order-heatmap`);
      url.searchParams.set('grain', grain);
      url.searchParams.set(COUNT_PARAM[grain], String(count));
      const res = await fetch(url.toString(), {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': selectedBrand.id },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error || data?.detail || `HTTP ${res.status}`);
      }
      const data = await res.json().catch(() => ({}));
      setPayload(data);
      setLastUpdated(new Date());
    } catch (e) {
      setPayload(null);
      setError(e?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBrand?.id, grain, count]);

  const periodsList = useMemo(() => payload?.periods || [], [payload]);
  const hoursList = useMemo(() => payload?.hours || [], [payload]);
  const sym = currencySymbol(payload?.currency);
  const activeGrain = payload?.grain || grain;
  const grainNoun = GRAIN_NOUN[activeGrain];
  const grainAdverb = GRAIN_ADVERB[activeGrain];

  const colorField = COLOR_FIELD[metric];
  const isFunnel = metric === 'funnel';
  const isCombo = metric === 'combo';
  const isMoney = metric === 'revenue' || metric === 'cost';

  // For the dual view we scale orders and spend independently (each on its own hue).
  const comboMax = useMemo(() => {
    const acc = { orders: 0, cost: 0 };
    for (const h of hoursList) {
      for (const p of periodsList) {
        const c = h.byPeriod?.[p.key] || {};
        if ((c.orders || 0) > acc.orders) acc.orders = c.orders;
        if ((c.cost || 0) > acc.cost) acc.cost = c.cost;
      }
    }
    return acc;
  }, [hoursList, periodsList]);

  // Global max of the color field → makes periods directly comparable.
  const maxVal = useMemo(() => {
    let m = 0;
    for (const h of hoursList) {
      for (const p of periodsList) {
        const v = h.byPeriod?.[p.key]?.[colorField] || 0;
        if (v > m) m = v;
      }
    }
    return m;
  }, [hoursList, periodsList, colorField]);

  const colorValue = (h, key) => Number(h.byPeriod?.[key]?.[colorField] || 0);
  const fmtCell = (v) =>
    isMoney
      ? v >= 1000
        ? `${Math.round(v / 1000)}K`
        : fmtNumber(Math.round(v))
      : fmtNumber(v);

  const hasData = periodsList.length > 0 && hoursList.length > 0;
  const baseCol = activeGrain === 'day' ? 80 : activeGrain === 'week' ? 56 : 64;
  const colMinWidth = isFunnel ? Math.max(baseCol, 92) : isCombo ? Math.max(baseCol, 86) : baseCol;
  const cardMinWidth = activeGrain === 'day' ? 132 : activeGrain === 'week' ? 128 : 150;

  return (
    <div className="p-6 space-y-6">
      <Card className="rounded-2xl overflow-hidden shadow-xl border border-gray-100">
        {/* Header */}
        <div className="p-6 bg-white border-b">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex items-center gap-4">
              <img src="/GA4logoicon.svg" alt="GA4" className="w-12 h-12 object-contain" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Hourly Order Analysis</h1>
                <p className="text-sm text-gray-500 mt-1">
                  Live from GA4 — how each hour of the day performs, {grainAdverb}.
                  {selectedBrand?.name ? ` · ${selectedBrand.name}` : ''}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              {/* Grain toggle (Monthly / Weekly) */}
              <div className="flex items-center bg-gray-100 rounded-lg p-1">
                {GRAINS.map((g) => {
                  const active = grain === g.key;
                  return (
                    <button
                      key={g.key}
                      type="button"
                      onClick={() => changeGrain(g.key)}
                      className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                        active ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'
                      }`}
                    >
                      {g.label}
                    </button>
                  );
                })}
              </div>
              {/* Metric toggle */}
              <div className="flex items-center bg-gray-100 rounded-lg p-1">
                {METRICS.map((m) => {
                  const Icon = m.icon;
                  const active = metric === m.key;
                  return (
                    <button
                      key={m.key}
                      type="button"
                      onClick={() => setMetric(m.key)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                        active ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {m.label}
                    </button>
                  );
                })}
              </div>
              {/* Period count select */}
              <select
                value={count}
                onChange={(e) => setCount(Number(e.target.value))}
                className="px-4 py-2 bg-white border rounded-xl text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {COUNT_OPTIONS[grain].map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={fetchData}
                disabled={loading}
                className="flex items-center justify-center w-10 h-10 bg-white border rounded-xl text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                title="Refresh"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          {error && (
            <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">{error}</div>
          )}

          {!selectedBrand?.id && (
            <div className="p-6 text-center text-gray-500">Please select a brand.</div>
          )}

          {/* Per-period summary strip (scrolls horizontally when it overflows) */}
          {hasData && (
            <div className="overflow-x-auto">
              <div className="flex gap-3" style={{ minWidth: '100%' }}>
                {periodsList.map((p) => (
                  <div
                    key={p.key}
                    className="rounded-xl border border-gray-100 bg-gray-50/60 p-3 flex-1"
                    style={{ minWidth: `${cardMinWidth}px` }}
                  >
                    <div className="text-xs font-semibold text-gray-500">{p.label}</div>
                    {isFunnel ? (
                      <div className="mt-1 flex items-center gap-3">
                        <div>
                          <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Checkout</div>
                          <div className="text-lg font-bold text-gray-700">{fmtNumber(p.checkouts)}</div>
                        </div>
                        <Filter className="w-4 h-4 text-gray-300" />
                        <div>
                          <div className="text-[10px] font-semibold uppercase tracking-wide text-blue-500">Purchase</div>
                          <div className="text-lg font-bold text-blue-600">{fmtNumber(p.orders)}</div>
                        </div>
                      </div>
                    ) : isCombo ? (
                      <div className="mt-1 flex items-center gap-4">
                        <div>
                          <div className="text-[10px] font-semibold uppercase tracking-wide text-blue-500">Orders</div>
                          <div className="text-lg font-bold text-blue-600">{fmtNumber(p.orders)}</div>
                        </div>
                        <div>
                          <div className="text-[10px] font-semibold uppercase tracking-wide text-orange-500">Spend</div>
                          <div className="text-lg font-bold text-orange-600">{fmtMoney(p.cost, sym)}</div>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="mt-1 text-lg font-bold text-gray-900">
                          {metric === 'revenue'
                            ? fmtMoney(p.revenue, sym)
                            : metric === 'cost'
                              ? fmtMoney(p.cost, sym)
                              : `${fmtNumber(p.orders)} orders`}
                        </div>
                        <div className="mt-1 text-xs text-gray-500 flex items-center gap-1">
                          {metric === 'cost' ? (
                            <span>G {fmtMoney(p.googleCost, sym)} · M {fmtMoney(p.metaCost, sym)}</span>
                          ) : (
                            <>
                              <Sun className="w-3.5 h-3.5 text-amber-500" />
                              Peak {String(p.peakHour).padStart(2, '0')}:00 · CR {fmtPercent(p.conversionRate)}
                            </>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Heatmap */}
          {hasData && (
            <div className="overflow-x-auto">
              <table className="border-separate" style={{ borderSpacing: '3px', minWidth: '100%' }}>
                <thead>
                  <tr>
                    <th className="text-left text-xs font-semibold text-gray-400 px-2 sticky left-0 bg-white z-10">Hour</th>
                    {periodsList.map((p) => (
                      <th
                        key={p.key}
                        className="text-center text-xs font-semibold text-gray-600 px-1 pb-1"
                        style={{ minWidth: `${colMinWidth}px` }}
                      >
                        {p.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {hoursList.map((h) => (
                    <tr key={h.hour}>
                      <td className="text-right text-xs font-medium text-gray-500 pr-2 sticky left-0 bg-white whitespace-nowrap z-10">
                        {String(h.hour).padStart(2, '0')}:00
                      </td>
                      {periodsList.map((p) => {
                        const cell = h.byPeriod?.[p.key] || {};
                        const cv = colorValue(h, p.key);
                        const t = maxVal > 0 ? cv / maxVal : 0;
                        const style = cellColor(t);
                        const tip =
                          `${p.label} ${String(h.hour).padStart(2, '0')}:00\n` +
                          `Orders: ${fmtNumber(cell.orders)}\n` +
                          `Revenue: ${fmtMoney(cell.revenue, sym)}\n` +
                          `Ad Spend: ${fmtMoney(cell.cost, sym)}\n` +
                          `  Google: ${fmtMoney(cell.googleCost, sym)}\n` +
                          `  Meta: ${fmtMoney(cell.metaCost, sym)}\n` +
                          `Checkouts: ${fmtNumber(cell.checkouts)}\n` +
                          `Sessions: ${fmtNumber(cell.sessions)}\n` +
                          `Conversion: ${fmtPercent(cell.conversionRate)}`;
                        const tdClass = isFunnel
                          ? 'h-12 py-1 px-1.5'
                          : isCombo
                            ? 'h-8 p-0 text-[9px]'
                            : 'h-7 text-[11px]';
                        const tdStyle = isCombo
                          ? { background: 'transparent', minWidth: `${colMinWidth}px` }
                          : { ...style, minWidth: `${colMinWidth}px` };
                        return (
                          <td
                            key={p.key}
                            title={tip}
                            className={`text-center font-medium rounded-md transition ${tdClass}`}
                            style={tdStyle}
                          >
                            {isCombo ? (
                              <div className="flex items-stretch gap-[2px] h-8">
                                <div
                                  className="flex-1 rounded-sm flex items-center justify-center leading-none"
                                  style={rampColor(comboMax.orders > 0 ? cell.orders / comboMax.orders : 0, HUE.blue)}
                                >
                                  {cell.orders > 0 ? fmtNumber(cell.orders) : ''}
                                </div>
                                <div
                                  className="flex-1 rounded-sm flex items-center justify-center leading-none"
                                  style={rampColor(comboMax.cost > 0 ? cell.cost / comboMax.cost : 0, HUE.orange)}
                                >
                                  {cell.cost > 0 ? compact(cell.cost) : ''}
                                </div>
                              </div>
                            ) : isFunnel ? (
                              (cell.checkouts > 0 || cell.orders > 0) ? (
                                <div className="flex flex-col gap-0.5">
                                  <div className="flex items-center justify-between gap-1">
                                    <span className="text-[8px] font-semibold uppercase opacity-60">Chk</span>
                                    <span className="text-sm font-bold leading-none">{fmtNumber(cell.checkouts)}</span>
                                  </div>
                                  <div className="flex items-center justify-between gap-1">
                                    <span className="text-[8px] font-semibold uppercase opacity-60">Buy</span>
                                    <span className="text-sm font-bold leading-none">{fmtNumber(cell.orders)}</span>
                                  </div>
                                </div>
                              ) : ''
                            ) : (
                              cv > 0 ? fmtCell(cv) : ''
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
              {isCombo ? (
                <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-gray-400">
                  <span className="flex items-center gap-2">
                    <span className="font-medium text-blue-600">Orders</span>
                    <div className="flex rounded-md overflow-hidden">
                      {[0.05, 0.25, 0.5, 0.75, 1].map((t) => (
                        <div key={t} className="w-5 h-3" style={{ background: rampColor(t, HUE.blue).background }} />
                      ))}
                    </div>
                  </span>
                  <span className="flex items-center gap-2">
                    <span className="font-medium text-orange-600">Ad Spend</span>
                    <div className="flex rounded-md overflow-hidden">
                      {[0.05, 0.25, 0.5, 0.75, 1].map((t) => (
                        <div key={t} className="w-5 h-3" style={{ background: rampColor(t, HUE.orange).background }} />
                      ))}
                    </div>
                  </span>
                  <span>· left = orders · right = Google + Meta spend (K = thousand {sym.trim()}) · each colored on its own scale</span>
                </div>
              ) : (
                <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-gray-400">
                  <span>Low</span>
                  <div className="flex rounded-md overflow-hidden">
                    {[0.05, 0.25, 0.5, 0.75, 1].map((t) => (
                      <div key={t} className="w-6 h-3" style={{ background: cellColor(t).background }} />
                    ))}
                  </div>
                  <span>High</span>
                  <span className="ml-2">
                    · {metric === 'revenue'
                      ? `K = thousand ${sym.trim()}`
                      : metric === 'cost'
                        ? `Google + Meta ad spend · K = thousand ${sym.trim()}`
                        : isFunnel
                          ? 'Chk = checkouts · Buy = purchases · color by purchases'
                          : 'order count'} · color scale shared across all {grainNoun} (comparable)
                  </span>
                </div>
              )}
            </div>
          )}

          {hasData && (
            <div className="text-xs text-gray-400">
              {payload?.startDate} – {payload?.endDate}
              {lastUpdated ? ` · Updated ${lastUpdated.toLocaleTimeString('en-US')}` : ''}
            </div>
          )}

          {!loading && !error && selectedBrand?.id && !hasData && (
            <div className="p-6 text-center text-gray-500">No hourly GA4 data found for this brand.</div>
          )}
        </div>
      </Card>
    </div>
  );
}
